import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn";
import EventIcon from "@mui/icons-material/Event";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useDispatch, useSelector } from "react-redux";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import homeImage from "../../assets/DTS2.png";
import { fetchNotApprovedLeavePriorList } from "../../actions/LeavePriorAction";

/* -------------------- Styles -------------------- */
const PageContainer = styled(Box)(() => ({
  maxWidth: "1000px",
  margin: "0 auto",
  padding: "10px 15px",
  boxSizing: "border-box",
  width: "100%",
  fontFamily: "'Poppins', sans-serif",
}));

const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: "16px",
  boxShadow: "0px 6px 15px rgba(0,0,0,0.12)",
  border: "1px solid #d6dce5",
  backgroundColor: "#ffffff",
  maxHeight: "650px",
  overflow: "auto",
  width: "100%",
  WebkitOverflowScrolling: "touch",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  padding: "12px 8px",
  fontSize: "13px",
  textAlign: "center",
  whiteSpace: "nowrap",
  [theme.breakpoints.down("sm")]: {
    padding: "8px 6px",
    fontSize: "11.5px",
  },
  "&.MuiTableCell-head": {
    [theme.breakpoints.down("sm")]: { whiteSpace: "normal", lineHeight: 1.2 },
    backgroundColor: "#154c79",
    color: "#ffffff",
    fontWeight: 600,
    borderBottom: "none",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "0.25s ease",
  "&:nth-of-type(odd)": { backgroundColor: "rgba(243, 246, 250, 0.6)" },
  "&:hover": { backgroundColor: "rgba(14, 60, 102, 0.09)" },
}));

const dateButtonSx = {
  width: "100%",
  height: "56px",
  borderRadius: "20px",
  backgroundColor: "#142c54",
  fontWeight: "bold",
  textTransform: "none",
  fontSize: { xs: "13px", sm: "15px" },
  display: "flex",
  flexDirection: "column",
  lineHeight: 1.2,
  "&:hover": { backgroundColor: "#0D47A1" },
};

const exportButtonSx = (bg, hover) => ({
  width: "100%",
  height: "52px",
  borderRadius: "20px",
  backgroundColor: bg,
  fontWeight: "bold",
  textTransform: "none",
  fontSize: { xs: "13px", sm: "15px" },
  "&:hover": { backgroundColor: hover },
  "&:disabled": { backgroundColor: "#cccccc", color: "#666666" },
});

// Local yyyy-MM-dd (toISOString shifts to UTC and can give the previous day in Sri Lanka)
const formatDate = (dateObj) => {
  const d = new Date(dateObj);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatTime = (dateObj) =>
  new Date(dateObj).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

// "2026-09-10" -> "10 Sep" (compact date for mobile table)
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const shortDate = (value) => {
  const [y, m, d] = String(value || "").split("-");
  if (!y || !m || !d) return value || "";
  return `${d} ${MONTHS[Number(m) - 1] || m}`;
};

const firstDayOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
};

/* -------------------- Component -------------------- */
export default function NotApprovedLeave() {
  const dispatch = useDispatch();

  const { notApprovedList, notApprovedListLoading, notApprovedListError } = useSelector(
    (state) => state.leavePrior
  );

  // "single" | "range"
  const [mode, setMode] = useState("single");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [fromDate, setFromDate] = useState(firstDayOfMonth());
  const [toDate, setToDate] = useState(new Date());
  // which picker is open: null | "single" | "from" | "to"
  const [pickerOpen, setPickerOpen] = useState(null);
  const [search, setSearch] = useState("");

  const isRange = mode === "range";
  const rangeInvalid = isRange && formatDate(fromDate) > formatDate(toDate);

  /* -------------------- Load -------------------- */
  useEffect(() => {
    if (isRange) {
      if (rangeInvalid) return;
      dispatch(fetchNotApprovedLeavePriorList({ fromDate: formatDate(fromDate), toDate: formatDate(toDate) }));
    } else {
      dispatch(fetchNotApprovedLeavePriorList({ date: formatDate(selectedDate) }));
    }
  }, [dispatch, isRange, rangeInvalid, selectedDate, fromDate, toDate]);

  const list = useMemo(() => (rangeInvalid ? [] : notApprovedList || []), [notApprovedList, rangeInvalid]);

  // Search by service no, name, location, designation
  // then sort: Location (A-Z) -> Designation (A-Z) -> Date -> Name
  const filteredList = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = !q
      ? [...list]
      : list.filter((item) =>
          [item.ServiceNo, item.Name, item.Location, item.Designation]
            .some((v) => String(v || "").toLowerCase().includes(q))
        );
    const cmp = (a, b) => String(a || "").localeCompare(String(b || ""), undefined, { sensitivity: "base" });
    return rows.sort(
      (a, b) =>
        cmp(a.Location, b.Location) ||
        cmp(a.Designation, b.Designation) ||
        cmp(a.Date, b.Date) ||
        cmp(a.Name, b.Name)
    );
  }, [list, search]);

  const periodText = isRange
    ? `${formatDate(fromDate)} to ${formatDate(toDate)}`
    : formatDate(selectedDate);

  const fileSuffix = isRange
    ? `${formatDate(fromDate)}_to_${formatDate(toDate)}`
    : formatDate(selectedDate);

  const canExport = !notApprovedListLoading && filteredList.length > 0;

  // Export columns (Date only in range mode)
  const exportColumns = isRange
    ? ["Location", "Date", "Service No", "Name", "Designation"]
    : ["Location", "Service No", "Name", "Designation"];

  const exportRows = () =>
    filteredList.map((item) =>
      isRange
        ? [item.Location || "", item.Date || "", item.ServiceNo || "", item.Name || "", item.Designation || ""]
        : [item.Location || "", item.ServiceNo || "", item.Name || "", item.Designation || ""]
    );

  /* -------------------- PDF EXPORT -------------------- */
  const handleExportPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const generatedAt = new Date();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.addImage(homeImage, "PNG", 0, 0, 210, 65);

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(21, 76, 121);
    doc.text("Leave Not Approved Report", pageWidth / 2, 75, { align: "center" });

    doc.setDrawColor(21, 76, 121);
    doc.setLineWidth(0.5);
    doc.line(15, 82, pageWidth - 15, 82);

    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    const infoY = 89;
    const drawLabel = (label, value, x, align = "left") => {
      doc.setFont("helvetica", "bold");
      const labelW = doc.getTextWidth(label);
      doc.setFont("helvetica", "normal");
      const valueW = doc.getTextWidth(value);
      let startX = x;
      if (align === "right") startX = x - labelW - valueW;
      if (align === "center") startX = x - (labelW + valueW) / 2;
      doc.setFont("helvetica", "bold");
      doc.text(label, startX, infoY);
      doc.setFont("helvetica", "normal");
      doc.text(value, startX + labelW, infoY);
    };
    drawLabel(isRange ? "Period - " : "Date - ", periodText, 15);
    drawLabel("Total - ", String(filteredList.length), pageWidth - 15, "right");

    autoTable(doc, {
      head: [exportColumns],
      body: exportRows(),
      startY: 95,
      styles: { fontSize: 10, cellPadding: 3.5, lineColor: [220, 220, 220], lineWidth: 0.1, valign: "middle" },
      headStyles: { fillColor: [21, 76, 121], textColor: [255, 255, 255], fontStyle: "bold", halign: "center" },
      alternateRowStyles: { fillColor: [248, 249, 252] },
      columnStyles: isRange
        ? { 0: { halign: "center", cellWidth: 22 }, 1: { halign: "center", cellWidth: 26 }, 2: { halign: "center", cellWidth: 26 } }
        : { 0: { halign: "center", cellWidth: 26 }, 1: { halign: "center", cellWidth: 30 } },
      margin: { left: 12, right: 12 },
      theme: "grid",
      didDrawPage: (data) => {
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.setFont("helvetica", "italic");
        doc.text(
          `Generated on ${formatDate(generatedAt)} at ${formatTime(generatedAt)}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: "center" }
        );
        doc.text(`Page ${data.pageNumber}`, pageWidth - 20, pageHeight - 10);
      },
    });

    doc.save(`Leave_Not_Approved_${fileSuffix}.pdf`);
  };

  /* -------------------- EXCEL EXPORT -------------------- */
  const handleExportExcel = async () => {
    const generatedAt = new Date();
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Leave Not Approved");
    const colCount = exportColumns.length;
    const lastCol = String.fromCharCode(64 + colCount);

    // Title
    sheet.mergeCells(`A1:${lastCol}1`);
    sheet.getCell("A1").value = "Leave Not Approved Report";
    sheet.getCell("A1").font = { bold: true, size: 16, color: { argb: "FF154C79" } };
    sheet.getCell("A1").alignment = { horizontal: "center" };

    // Info
    sheet.mergeCells(`A2:${lastCol}2`);
    sheet.getCell("A2").value = `${isRange ? "Period" : "Date"}: ${periodText}    |    Total: ${filteredList.length}`;
    sheet.getCell("A2").alignment = { horizontal: "center" };

    sheet.mergeCells(`A3:${lastCol}3`);
    sheet.getCell("A3").value = `Generated on ${formatDate(generatedAt)} at ${formatTime(generatedAt)}`;
    sheet.getCell("A3").font = { italic: true, size: 9, color: { argb: "FF888888" } };
    sheet.getCell("A3").alignment = { horizontal: "center" };

    // Header
    const headerRow = sheet.addRow([]);
    headerRow.height = 4;
    const header = sheet.addRow(exportColumns);
    header.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF154C79" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } };
    });
    header.height = 22;

    // Rows
    const nameIdx = exportColumns.indexOf("Name") + 1;
    const desigIdx = exportColumns.indexOf("Designation") + 1;
    exportRows().forEach((r, i) => {
      const row = sheet.addRow(r);
      row.eachCell((cell, colNumber) => {
        cell.alignment = {
          horizontal: colNumber === nameIdx || colNumber === desigIdx ? "left" : "center",
          vertical: "middle",
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FFDDDDDD" } },
          bottom: { style: "thin", color: { argb: "FFDDDDDD" } },
          left: { style: "thin", color: { argb: "FFDDDDDD" } },
          right: { style: "thin", color: { argb: "FFDDDDDD" } },
        };
        if (i % 2 === 1) {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F6FA" } };
        }
      });
    });

    // Column widths
    sheet.columns.forEach((col, idx) => {
      const name = exportColumns[idx];
      col.width = name === "Name" ? 32 : name === "Designation" ? 28 : 14;
    });

    // Freeze header
    sheet.views = [{ state: "frozen", ySplit: 5 }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Leave_Not_Approved_${fileSuffix}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /* -------------------- Picker helpers -------------------- */
  const pickerValue =
    pickerOpen === "from" ? fromDate : pickerOpen === "to" ? toDate : selectedDate;

  const handlePickerChange = (date) => {
    if (date) {
      if (pickerOpen === "from") setFromDate(date);
      else if (pickerOpen === "to") setToDate(date);
      else setSelectedDate(date);
    }
    setPickerOpen(null);
  };

  const colCount = isRange ? 5 : 4;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageContainer>
        <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#0E3C66", textAlign: "center", mb: 2, letterSpacing: "1px" }}>
          Leave Not Approved
        </Typography>

        {/* ── Mode: Single Date | Date Range ── */}
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={mode}
          onChange={(e, val) => val && setMode(val)}
          sx={{
            mb: 1.5,
            backgroundColor: "#eef2f7",
            borderRadius: "16px",
            p: "4px",
            "& .MuiToggleButtonGroup-grouped": {
              border: "none",
              borderRadius: "12px !important",
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "13px", sm: "14px" },
              color: "#142c54",
              height: "42px",
              gap: 1,
            },
            "& .Mui-selected": {
              backgroundColor: "#142c54 !important",
              color: "#fff !important",
            },
          }}
        >
          <ToggleButton value="single">
            <EventIcon fontSize="small" /> Single Date
          </ToggleButton>
          <ToggleButton value="range">
            <DateRangeIcon fontSize="small" /> Date Range
          </ToggleButton>
        </ToggleButtonGroup>

        {/* ── Date buttons ── */}
        {!isRange ? (
          <Box sx={{ mb: 1.5 }}>
            <Button variant="contained" startIcon={<CalendarTodayIcon />} onClick={() => setPickerOpen("single")} sx={{ ...dateButtonSx, flexDirection: "row" }}>
              {formatDate(selectedDate)}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: rangeInvalid ? 0.5 : 1.5 }}>
            <Button variant="contained" onClick={() => setPickerOpen("from")} sx={dateButtonSx}>
              <Box component="span" sx={{ fontSize: "11px", fontWeight: 500, opacity: 0.8 }}>From</Box>
              {formatDate(fromDate)}
            </Button>
            <Button variant="contained" onClick={() => setPickerOpen("to")} sx={dateButtonSx}>
              <Box component="span" sx={{ fontSize: "11px", fontWeight: 500, opacity: 0.8 }}>To</Box>
              {formatDate(toDate)}
            </Button>
          </Box>
        )}

        {rangeInvalid && (
          <Typography sx={{ color: "#c62828", fontSize: "12px", textAlign: "center", mb: 1.5 }}>
            "From" date must be on or before "To" date
          </Typography>
        )}

        {pickerOpen && (
          <DatePicker
            open
            value={pickerValue}
            onChange={handlePickerChange}
            onClose={() => setPickerOpen(null)}
            minDate={pickerOpen === "to" ? fromDate : undefined}
            maxDate={pickerOpen === "from" ? toDate : undefined}
            slotProps={{ textField: { sx: { display: "none" } } }}
          />
        )}

        {/* ── Total | Export PDF | Export Excel ── */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" }, gap: 1.5, mb: 1.5 }}>
          <Box
            sx={{
              gridColumn: { xs: "1 / -1", sm: "auto" },
              background: "#e8f0fa",
              border: "1px solid #bbd0ea",
              borderRadius: "20px",
              height: "52px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: { xs: "13px", sm: "15px" },
              color: "#0E3C66",
            }}
          >
            Total: {search.trim() ? `${filteredList.length} / ${list.length}` : list.length}
          </Box>
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPDF}
            disabled={!canExport}
            sx={exportButtonSx("#c62828", "#8e0000")}
          >
            PDF
          </Button>
          <Button
            variant="contained"
            startIcon={<GridOnIcon />}
            onClick={handleExportExcel}
            disabled={!canExport}
            sx={exportButtonSx("#2e7d32", "#1b5e20")}
          >
            Excel
          </Button>
        </Box>

        {/* ── Search ── */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search service no, name, location, designation..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "14px", height: "48px", backgroundColor: "#fff" } }}
        />

        {/* ── Table ── */}
        <StyledTableContainer component={Paper}>
          <Table stickyHeader size="small" sx={{ width: "100%", tableLayout: { xs: "fixed", sm: "auto" } }}>
            <TableHead>
              <TableRow>
                <StyledTableCell sx={{ width: { xs: isRange ? "17%" : "16%", sm: "auto" } }}>Location</StyledTableCell>
                {isRange && <StyledTableCell sx={{ width: { xs: "13%", sm: "auto" } }}>Date</StyledTableCell>}
                <StyledTableCell sx={{ width: { xs: isRange ? "16%" : "22%", sm: "auto" } }}>Service No</StyledTableCell>
                <StyledTableCell sx={{ width: { xs: isRange ? "28%" : "34%", sm: "auto" } }}>Name</StyledTableCell>
                <StyledTableCell sx={{ width: { xs: isRange ? "26%" : "28%", sm: "auto" } }}>Designation</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notApprovedListLoading && (
                <TableRow>
                  <TableCell colSpan={colCount} align="center">Loading...</TableCell>
                </TableRow>
              )}

              {!notApprovedListLoading && notApprovedListError && (
                <TableRow>
                  <TableCell colSpan={colCount} align="center" sx={{ color: "#c62828" }}>{notApprovedListError}</TableCell>
                </TableRow>
              )}

              {!notApprovedListLoading && !notApprovedListError && filteredList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={colCount} align="center">
                    {list.length === 0
                      ? isRange
                        ? "No pending leave for this date range"
                        : "No pending leave for this date"
                      : "No matching employees"}
                  </TableCell>
                </TableRow>
              )}

              {!notApprovedListLoading &&
                filteredList.map((item) => (
                  <StyledTableRow key={`${item.BarcodeNo}-${item.Date}`}>
                    <StyledTableCell>{item.Location}</StyledTableCell>
                    {isRange && (
                      <StyledTableCell>
                        <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>{shortDate(item.Date)}</Box>
                        <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{item.Date}</Box>
                      </StyledTableCell>
                    )}
                    <StyledTableCell>{item.ServiceNo}</StyledTableCell>
                    <StyledTableCell sx={{ textAlign: "left", whiteSpace: { xs: "normal", sm: "nowrap" }, wordBreak: "normal", overflowWrap: "anywhere" }}>
                      {item.Name}
                    </StyledTableCell>
                    <StyledTableCell sx={{ textAlign: "left", whiteSpace: { xs: "normal", sm: "nowrap" }, wordBreak: "normal", overflowWrap: "anywhere" }}>
                      {item.Designation}
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </PageContainer>
    </LocalizationProvider>
  );
}